// src/pages/admin/AdminOrders.js — with Export to Excel feature
import React, { useState, useCallback, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { ordersApi, unwrapPage, getErrorMessage } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { Badge, Modal, Button, Select } from '../../components/ui';
import { ErrorBox, TableRowSkeleton } from '../../components/common/ErrorBoundary';

const STATUS_OPTS = ['','pending','confirmed','processing','shipped','delivered','cancelled'];
const STATUS_BADGE = {
  pending:'amber', confirmed:'blue', processing:'purple',
  shipped:'indigo', delivered:'green', cancelled:'red',
};

/* ── Excel export helper ──────────────────────────────────────────────────── */
function exportToExcel(orders) {
  // Build CSV content (no library needed — pure JS)
  const headers = [
    'Order ID', 'Customer Name', 'Email', 'Phone', 'Address',
    'Items', 'Subtotal (₹)', 'Discount (₹)', 'Total (₹)',
    'Coupon Code', 'Status', 'Notes', 'Date',
  ];

  const rows = orders.map(o => [
    `#${o.id}`,
    o.name || '',
    o.email || '',
    o.phone || '',
    (o.address || '').replace(/\n/g, ' '),
    (o.items || []).map(i => `${i.product_name} x${i.quantity}`).join(' | '),
    parseFloat(o.subtotal || 0).toFixed(2),
    parseFloat(o.discount_amount || 0).toFixed(2),
    parseFloat(o.total || 0).toFixed(2),
    o.coupon_code || '',
    o.status || '',
    (o.notes || '').replace(/\n/g, ' '),
    new Date(o.created_at).toLocaleString('en-IN'),
  ]);

  // CSV escape helper
  const esc = (val) => {
    const s = String(val);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };

  const csvContent = [headers, ...rows]
    .map(row => row.map(esc).join(','))
    .join('\n');

  // Add BOM for Excel UTF-8 recognition (₹ symbol needs this)
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href     = url;
  link.download = `lakshmi_crackers_orders_${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function exportCustomers(orders) {
  // Extract unique customers from orders
  const seen = new Set();
  const customers = [];
  orders.forEach(o => {
    if (!seen.has(o.email)) {
      seen.add(o.email);
      customers.push({
        name:    o.name,
        email:   o.email,
        phone:   o.phone,
        address: (o.address || '').replace(/\n/g, ' '),
        orders:  orders.filter(x => x.email === o.email).length,
        spent:   orders.filter(x => x.email === o.email && x.status !== 'cancelled')
                       .reduce((s, x) => s + parseFloat(x.total || 0), 0).toFixed(2),
        last_order: new Date(
          Math.max(...orders.filter(x => x.email === o.email).map(x => new Date(x.created_at)))
        ).toLocaleString('en-IN'),
      });
    }
  });

  const headers = ['Name', 'Email', 'Phone', 'Address', 'Total Orders', 'Total Spent (₹)', 'Last Order'];
  const rows = customers.map(c => [c.name, c.email, c.phone, c.address, c.orders, c.spent, c.last_order]);

  const esc = (val) => {
    const s = String(val);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };

  const csvContent = [headers, ...rows].map(row => row.map(esc).join(',')).join('\n');
  const bom  = '\uFEFF';
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href     = url;
  link.download = `lakshmi_crackers_customers_${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}


/* ── Order PDF generator ─────────────────────────────────────────────────── */
function generateOrderPDF(order) {
  const date     = new Date(order.created_at).toLocaleString('en-IN', {
    day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit',
  });
  const items    = order.items || [];
  const subtotal = parseFloat(order.subtotal       || 0);
  const discount = parseFloat(order.discount_amount || 0);
  const total    = parseFloat(order.total          || 0);

  // Item rows — plain, no alternating colour
  const itemRows = items.map((item, i) =>
    '<tr style="background:' + (i%2===0?'#f9fafb':'#ffffff') + '">' +
      '<td style="padding:7px 10px;border:1px solid #d1d5db;font-size:11px;color:#111">' + item.product_name + '</td>' +
      '<td style="padding:7px 10px;border:1px solid #d1d5db;text-align:center;font-size:11px;font-weight:700;color:#111;white-space:nowrap">x ' + item.quantity + '</td>' +
      '<td style="padding:7px 10px;border:1px solid #d1d5db;text-align:right;font-size:11px;font-weight:700;color:#111">Rs.' + parseFloat(item.line_total||0).toFixed(2) + '</td>' +
    '</tr>'
  ).join('');

  const html =
    '<!DOCTYPE html><html><head><meta charset="UTF-8"/>' +
    '<title>Order #' + order.id + ' - Lakshmi Crackers</title>' +
    '<style>' +
      '*{margin:0;padding:0;box-sizing:border-box}' +
      'body{font-family:Arial,sans-serif;font-size:12px;color:#111;background:#fff}' +
      '.wrap{max-width:680px;margin:0 auto;padding:24px}' +
      'table{width:100%;border-collapse:collapse}' +
      'th{background:#1f2937;color:#fff;padding:6px 8px;font-size:10px;text-align:left;text-transform:uppercase;letter-spacing:.5px}' +
      'th.r{text-align:right} th.c{text-align:center}' +
      '.lbl{font-size:10px;color:#6b7280;margin-bottom:2px}' +
      '.val{font-size:12px;font-weight:600;color:#111}' +
      '.sec{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;color:#6b7280;border-bottom:1px solid #e5e7eb;padding-bottom:3px;margin-bottom:8px}' +
      '@media print{.no-print{display:none!important}@page{margin:12mm;size:A4}}' +
    '</style></head><body><div class="wrap">' +

    // Print button
    '<div class="no-print" style="text-align:right;margin-bottom:14px;display:flex;gap:8px;justify-content:flex-end">' +
      '<button onclick="window.print()" style="background:#1f2937;color:#fff;border:none;padding:7px 18px;border-radius:6px;font-size:12px;font-weight:600;cursor:pointer">Print / Save as PDF</button>' +
      '<button onclick="window.close()" style="background:#f3f4f6;color:#374151;border:none;padding:7px 14px;border-radius:6px;font-size:12px;cursor:pointer">Close</button>' +
    '</div>' +

    // Header — two columns: shop info | order info
    '<table style="margin-bottom:14px"><tr>' +
      '<td style="vertical-align:top;width:60%">' +
        '<div style="font-size:16px;font-weight:700;color:#111;margin-bottom:3px">LAKSHMI CRACKERS</div>' +
        '<div style="font-size:10px;color:#6b7280;line-height:1.7">' +
          '4/214, Near Enammeenatchipuram Bus Stop<br/>' +
          'Vembakottai, Sivakasi, Tamil Nadu - 626131<br/>' +
          'Ph: +91 63748 01083<br/>' +
          'Email: lakshmicrackersonline@gmail.com<br/>' +
          'Web: www.lakshmicrackers.com' +
        '</div>' +
      '</td>' +
      '<td style="vertical-align:top;text-align:right">' +
        '<div style="font-size:18px;font-weight:700;color:#111">ORDER #' + order.id + '</div>' +
        '<div style="font-size:10px;color:#6b7280;margin-top:3px">Date: ' + date + '</div>' +
        '<div style="margin-top:6px;display:inline-block;background:#1f2937;color:#fff;padding:3px 10px;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.5px">' + (order.status||'pending') + '</div>' +
      '</td>' +
    '</tr></table>' +

    '<hr style="border:none;border-top:2px solid #111;margin-bottom:12px"/>' +

    // Customer + Address — two columns
    '<table style="margin-bottom:14px"><tr>' +
      '<td style="vertical-align:top;width:50%;padding-right:16px">' +
        '<div class="sec">Customer Details</div>' +
        '<div class="lbl">Name</div><div class="val">' + (order.name||'—') + '</div>' +
        '<div class="lbl" style="margin-top:5px">Phone</div><div class="val">' + (order.phone||'—') + '</div>' +
        '<div class="lbl" style="margin-top:5px">Email</div><div class="val" style="font-size:11px">' + (order.email||'—') + '</div>' +
      '</td>' +
      '<td style="vertical-align:top;width:50%;padding-left:16px;border-left:1px solid #e5e7eb">' +
        '<div class="sec">Delivery Address</div>' +
        '<div style="font-size:11px;color:#111;line-height:1.7;white-space:pre-line">' + (order.address||'—') + '</div>' +
      '</td>' +
    '</tr></table>' +

    // Items table
    '<div class="sec" style="margin-bottom:6px">Order Items</div>' +
    '<table style="margin-bottom:12px;width:100%;border-collapse:collapse;border:1px solid #d1d5db">' +
      '<thead><tr style="background:#1f2937">' +
        '<th style="padding:8px 10px;font-size:10px;text-align:left;color:#fff;border:1px solid #374151;font-weight:700;text-transform:uppercase;letter-spacing:.5px">Product</th>' +
        '<th style="padding:8px 10px;font-size:10px;text-align:center;color:#fff;border:1px solid #374151;font-weight:700;text-transform:uppercase;letter-spacing:.5px;white-space:nowrap">Qty</th>' +
        '<th style="padding:8px 10px;font-size:10px;text-align:right;color:#fff;border:1px solid #374151;font-weight:700;text-transform:uppercase;letter-spacing:.5px">Amount (Rs.)</th>' +
      '</tr></thead>' +
      '<tbody>' + itemRows + '</tbody>' +
    '</table>' +

    // Summary — right aligned
    '<table style="margin-bottom:12px"><tr><td style="width:60%"></td>' +
      '<td style="width:40%">' +
        '<table>' +
          '<tr><td style="padding:3px 0;font-size:11px;color:#6b7280">Subtotal</td><td style="padding:3px 0;font-size:11px;text-align:right">Rs.' + subtotal.toFixed(2) + '</td></tr>' +
          (discount > 0 ? '<tr><td style="padding:3px 0;font-size:11px;color:#15803d">Discount (' + (order.coupon_code||'') + ')</td><td style="padding:3px 0;font-size:11px;text-align:right;color:#15803d">- Rs.' + discount.toFixed(2) + '</td></tr>' : '') +
          '<tr style="border-top:1.5px solid #111"><td style="padding:5px 0;font-size:13px;font-weight:700">Total</td><td style="padding:5px 0;font-size:13px;font-weight:700;text-align:right">Rs.' + total.toFixed(2) + '</td></tr>' +
        '</table>' +
      '</td>' +
    '</tr></table>' +

    (order.notes ? '<div class="sec">Notes</div><div style="font-size:11px;color:#374151;margin-bottom:12px">' + order.notes + '</div>' : '') +

    // Footer
    '<hr style="border:none;border-top:1px solid #e5e7eb;margin-bottom:8px"/>' +
    '<div style="font-size:10px;color:#9ca3af;text-align:center;line-height:1.7">' +
      'This is a system-generated order document. Payment will be confirmed via phone/WhatsApp within 24 hours.<br/>' +
      'Lakshmi Crackers, Sivakasi | +91 63748 01083 | lakshmicrackersonline@gmail.com' +
    '</div>' +

    '</div></body></html>';

  const win = window.open('', '_blank', 'width=780,height=700,scrollbars=yes');
  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 600);
}

/* ── Order detail modal ───────────────────────────────────────────────────── */
function OrderModal({ order, onUpdated, onClose }) {
  const { toast }            = useToast();
  const [status,  setStatus] = useState(order.status);
  const [notes,   setNotes]  = useState(order.notes || '');
  const [loading, setLoad]   = useState(false);
  const [error,   setError]  = useState('');

  const save = async () => {
    setLoad(true); setError('');
    try {
      await ordersApi.adminUpdate(order.id, { status, notes });
      toast.success(`Order #${order.id} updated to "${status}".`);
      onUpdated();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setLoad(false);
    }
  };

  return (
    <Modal open title={`Order #${order.id}`} onClose={onClose} maxWidth="max-w-2xl">
      <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-xl p-4 text-sm mb-5">
        {[['Name', order.name],['Phone', order.phone],['Email', order.email],
          ['Date', new Date(order.created_at).toLocaleString('en-IN')]].map(([l,v]) => (
          <div key={l}>
            <span className="text-gray-400 font-medium">{l}: </span>
            <span className="font-bold text-gray-800">{v}</span>
          </div>
        ))}
        <div className="col-span-2">
          <span className="text-gray-400 font-medium">Address: </span>
          <span className="font-bold text-gray-800">{order.address}</span>
        </div>
      </div>

      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Items</p>
          <span className="bg-red-100 text-red-700 text-xs font-black px-2.5 py-0.5 rounded-full">
            {order.items?.length ?? 0} {order.items?.length === 1 ? 'item' : 'items'}
          </span>
        </div>
        <div className="space-y-2">
          {order.items?.map(item => (
            <div key={item.id} className="flex justify-between items-center
              bg-amber-50 rounded-xl px-4 py-2.5 text-sm">
              <span className="font-medium text-gray-700 flex items-center gap-2">
                {item.product_name}
                <span className="bg-gray-200 text-gray-800 font-black text-xs
                  px-2 py-0.5 rounded-md min-w-[28px] text-center">
                  x{item.quantity}
                </span>
              </span>
              <span className="font-black text-gray-800">₹{parseFloat(item.line_total).toFixed(0)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-1.5 mb-5">
        <div className="flex justify-between text-gray-500">
          <span>Subtotal</span><span>₹{parseFloat(order.subtotal).toFixed(0)}</span>
        </div>
        {parseFloat(order.discount_amount) > 0 && (
          <div className="flex justify-between text-green-600 font-semibold">
            <span>Discount ({order.coupon_code})</span>
            <span>− ₹{parseFloat(order.discount_amount).toFixed(0)}</span>
          </div>
        )}
        <div className="flex justify-between font-black text-gray-900 border-t border-gray-200 pt-2">
          <span>Total</span>
          <span className="text-red-700 text-lg">₹{parseFloat(order.total).toFixed(0)}</span>
        </div>
      </div>

      <Select
        label="Update Status"
        options={STATUS_OPTS.filter(Boolean).map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
        value={status}
        onChange={e => setStatus(e.target.value)}
      />
      <div className="mt-3">
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Internal Notes</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
          className="w-full border border-gray-200 rounded-xl py-2 px-3 text-sm
            focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"/>
      </div>

      {error && (
        <div className="mt-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-2">
          {error}
        </div>
      )}

      <div className="flex gap-3 mt-5 pt-5 border-t border-gray-100">
        <Button variant="outline" className="flex-1" onClick={onClose} disabled={loading}>Cancel</Button>
        <button onClick={() => generateOrderPDF(order)}
          className="flex items-center justify-center gap-2 border-2 border-red-200
            text-red-700 hover:bg-red-50 font-bold px-4 py-2.5 rounded-2xl
            text-sm transition-all duration-200 hover:-translate-y-0.5">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586
                a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          Download PDF
        </button>
        <Button className="flex-1" onClick={save} loading={loading}>Update Order</Button>
      </div>
    </Modal>
  );
}

/* ── Export dropdown ──────────────────────────────────────────────────────── */
function ExportMenu({ orders, loading: fetchingAll, onFetchAll }) {
  const [open, setOpen] = useState(false);
  const { toast }       = useToast();
  const [exporting, setExporting] = useState(false);

  const handleExport = async (type) => {
    setOpen(false);
    if (orders.length === 0) {
      toast.warning('No orders to export.');
      return;
    }
    setExporting(true);
    try {
      // Fetch ALL orders (not just current page) for full export
      const allOrders = await onFetchAll();
      if (type === 'orders') {
        exportToExcel(allOrders);
        toast.success(`✅ Exported ${allOrders.length} orders to CSV`);
      } else {
        exportCustomers(allOrders);
        const uniqueEmails = new Set(allOrders.map(o => o.email)).size;
        toast.success(`✅ Exported ${uniqueEmails} customers to CSV`);
      }
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        disabled={exporting}
        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white
          font-bold text-sm px-4 py-2.5 rounded-xl transition-all duration-200
          hover:-translate-y-0.5 active:scale-95 shadow-md shadow-green-600/25
          disabled:opacity-50 disabled:cursor-not-allowed">
        {exporting
          ? <><span className="animate-spin text-xs">✦</span> Exporting…</>
          : <><span>📥</span> Export</>
        }
        {!exporting && (
          <svg className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7"/>
          </svg>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)}/>
          {/* Dropdown */}
          <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl
            shadow-2xl border border-gray-100 overflow-hidden z-20 animate-scale-in">
            <div className="px-4 py-2.5 border-b border-gray-100">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Export as CSV</p>
            </div>
            <button onClick={() => handleExport('orders')}
              className="w-full text-left px-4 py-3 hover:bg-green-50 transition-colors
                flex items-center gap-3 group">
              <span className="text-xl">📦</span>
              <div>
                <p className="text-sm font-bold text-gray-800 group-hover:text-green-700">All Orders</p>
                <p className="text-xs text-gray-400">ID, customer, items, total, status</p>
              </div>
            </button>
            <button onClick={() => handleExport('customers')}
              className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors
                flex items-center gap-3 group border-t border-gray-50">
              <span className="text-xl">👥</span>
              <div>
                <p className="text-sm font-bold text-gray-800 group-hover:text-blue-700">Customer List</p>
                <p className="text-xs text-gray-400">Unique customers with order history</p>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Main page ────────────────────────────────────────────────────────────── */
export default function AdminOrders() {
  const { toast }              = useToast();
  const [orders,   setOrders]  = useState([]);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [bulkLoad,   setBulkLoad]  = useState(false);
  const [loading,  setLoading] = useState(true);
  const [error,    setError]   = useState(null);
  const [filter,   setFilter]  = useState('');
  const [search,   setSearch]  = useState('');
  const [selected, setSelected]= useState(null);

  const load = useCallback((signal) => {
    setLoading(true); setError(null);
    const params = filter ? { status: filter } : {};
    ordersApi.adminList(params)
      .then(res => {
        if (signal && signal.aborted) return;
        setOrders(unwrapPage(res).data); setError(null);
      })
      .catch(err => {
        if (signal && signal.aborted) return;
        setError(err); setOrders([]);
      })
      .finally(() => {
        if (signal && signal.aborted) return;
        setLoading(false);
      });
  }, [filter]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Client-side search — filter by name or phone instantly
  const filteredOrders = search.trim()
    ? orders.filter(o => {
        const q = search.trim().toLowerCase();
        return (
          (o.name  || '').toLowerCase().includes(q) ||
          (o.phone || '').replace(/\D/g,'').includes(q.replace(/\D/g,''))
        );
      })
    : orders;

  // Fetch ALL orders for export (no status filter, large page)
  const fetchAllForExport = async () => {
    const res = await ordersApi.adminList({ page_size: 10000 });
    return unwrapPage(res).data;
  };

  const toggleSelect = (id) => setCheckedIds(prev => {
    const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s;
  });
  const toggleAll = () => setCheckedIds(
    checkedIds.size === orders.length ? new Set() : new Set(orders.map(o => o.id))
  );
  const handleBulk = async (action) => {
    if (!checkedIds.size) return;
    const label = action === 'delete' ? 'DELETE' : `set status to "${action}"`;
    if (!window.confirm(`Bulk ${label} for ${checkedIds.size} order(s)?`)) return;
    setBulkLoad(true);
    try {
      const res = await ordersApi.adminBulkOrders({ ids: [...checkedIds], action });
      toast.success(res?.message || `${checkedIds.size} order(s) updated.`);
      setCheckedIds(new Set()); load(new AbortController().signal);
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setBulkLoad(false); }
  };

  return (
    <AdminLayout title="📦 Orders">
      {/* Header: filters + search + export */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTS.map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold capitalize transition-colors
                ${filter === s
                  ? 'bg-red-600 text-white shadow-md'
                  : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
              {s || 'All'}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {/* Search by name or phone */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z"/>
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name or mobile…"
              className="border border-gray-200 bg-white rounded-xl py-2 pl-9 pr-10 text-sm
                focus:outline-none focus:ring-2 focus:ring-red-400 w-52 transition-all"/>
            {search && (
              <button onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400
                  hover:text-gray-600 transition-colors">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293
                    a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10
                    11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0
                    010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>
            )}
          </div>
          {/* Export button */}
          <ExportMenu orders={orders} onFetchAll={fetchAllForExport} />
        </div>
      </div>

      {/* Search result count */}
      {search.trim() && (
        <p className="text-xs text-gray-400 mb-3">
          {filteredOrders.length === 0
            ? 'No orders found for that search.'
            : `Showing ${filteredOrders.length} of ${orders.length} orders`}
        </p>
      )}

      {/* Bulk action bar */}
      {checkedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3">
          <span className="text-blue-700 font-black text-sm">{checkedIds.size} selected</span>
          <div className="flex flex-wrap gap-2 ml-2">
            {['confirmed','processing','shipped','delivered','cancelled','delete'].map(action => (
              <button key={action} onClick={() => handleBulk(action)} disabled={bulkLoad}
                className={`font-bold text-xs px-3 py-1.5 rounded-xl transition-colors disabled:opacity-50
                  ${action==='delete' ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : action==='delivered' ? 'bg-green-100 text-green-700 hover:bg-green-200'
                  : action==='cancelled' ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}>
                {action === 'delete' ? '🗑 Delete' : `→ ${action.charAt(0).toUpperCase()+action.slice(1)}`}
              </button>
            ))}
          </div>
          <button onClick={() => setCheckedIds(new Set())}
            className="ml-auto text-gray-400 hover:text-gray-600 text-xs font-bold">
            ✕ Clear
          </button>
        </div>
      )}
      {error && !loading && <ErrorBox error={error} onRetry={load} className="mb-4"/>}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-400 text-[11px] uppercase tracking-widest">
                <th className="px-4 py-3"><input type="checkbox" checked={orders.length > 0 && checkedIds.size === orders.length} onChange={toggleAll} className="w-4 h-4 accent-red-600 cursor-pointer"/></th>
                <th className="px-5 py-3 text-left">ID</th>
                <th className="px-5 py-3 text-left">Customer</th>
                <th className="px-5 py-3 text-left">Phone</th>
                <th className="px-5 py-3 text-left">Items</th>
                <th className="px-5 py-3 text-left">Total</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-left">Date</th>
                <th className="px-5 py-3 text-left">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(6)].map((_,i) => <TableRowSkeleton key={i} cols={9}/>)
              ) : filteredOrders.length === 0 && !error ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-gray-400 text-sm">No orders found</td>
                </tr>
              ) : filteredOrders.map(o => (
                <tr key={o.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4"><input type="checkbox" checked={checkedIds.has(o.id)} onChange={() => toggleSelect(o.id)} className="w-4 h-4 accent-red-600 cursor-pointer"/></td>
                  <td className="px-5 py-4 font-black text-gray-600">#{o.id}</td>
                  <td className="px-5 py-4">
                    <p className="font-bold text-gray-800">{o.name}</p>
                    <p className="text-xs text-gray-400">{o.email}</p>
                  </td>
                  <td className="px-5 py-4 text-gray-500">{o.phone}</td>
                  <td className="px-5 py-4 text-gray-500">{o.items?.length ?? 0}</td>
                  <td className="px-5 py-4 font-black text-gray-800"
                    style={{ fontFamily: '"Plus Jakarta Sans",sans-serif' }}>
                    ₹{parseFloat(o.total).toFixed(0)}
                  </td>
                  <td className="px-5 py-4">
                    <Badge color={STATUS_BADGE[o.status] || 'gray'}>{o.status}</Badge>
                  </td>
                  <td className="px-5 py-4 text-gray-400 text-xs">
                    {new Date(o.created_at).toLocaleDateString('en-IN')}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <button onClick={() => setSelected(o)}
                        className="text-red-600 hover:text-red-800 font-bold text-xs transition-colors">
                        Manage
                      </button>
                      <button onClick={() => generateOrderPDF(o)}
                        title="Download Order PDF"
                        className="flex items-center gap-1 text-gray-400 hover:text-red-600
                          font-bold text-xs transition-colors">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2
                              h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                        </svg>
                        PDF
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <OrderModal order={selected} onUpdated={load} onClose={() => setSelected(null)}/>
      )}
    </AdminLayout>
  );
}
